import { Injectable, inject } from '@angular/core';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { OfflineCacheService, SELECTORS_PREFETCH_KEY } from './offline-cache.service';

const PREFETCH_INTERVAL_MS = 15 * 60 * 1000;

/**
 * Pulls the full superset of reference catalogs (clients/products/categories/providers/
 * measurements/pay_methods/users/offices/companies) into the read cache, so a user can
 * create a document/ticket/purchase offline even for a client/product they hadn't visited
 * yet this session - not just whatever they happened to browse (cache-as-you-go alone would
 * miss that). `documents` is deliberately excluded: it's a large, growing dataset, not a
 * bounded reference catalog, and isn't needed to create a new record offline.
 */
@Injectable({
    providedIn: 'root'
})
export class PrefetchService {

    private apiService = inject(ApiService);
    private cache = inject(OfflineCacheService);

    private intervalHandle: ReturnType<typeof setInterval> | null = null;
    private onlineListenerAttached = false;

    /** Call on login/session-resume. Safe to call again (e.g. re-login after logout, or a superadmin company switch via `run()`) without leaking timers/listeners. */
    start(): void {
        this.run();
        if (this.intervalHandle) clearInterval(this.intervalHandle);
        this.intervalHandle = setInterval(() => {
            if (navigator.onLine) this.run();
        }, PREFETCH_INTERVAL_MS);
        if (!this.onlineListenerAttached) {
            window.addEventListener('online', () => this.run());
            this.onlineListenerAttached = true;
        }
    }

    /** Call on logout, so a background tab left signed out doesn't keep hitting the API (and tripping ErrorInterceptor's 401 reload) every cycle. */
    stop(): void {
        if (this.intervalHandle) {
            clearInterval(this.intervalHandle);
            this.intervalHandle = null;
        }
    }

    async run(): Promise<void> {
        if (!navigator.onLine) return;
        const config = {
            companies: {}, clients: {}, products: {}, categories: {},
            providers: {}, measurements: {}, pay_methods: {}, users: {}, offices: {},
        };
        try {
            const data = await new Promise<any>((resolve, reject) => {
                this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({ next: resolve, error: reject });
            });
            await this.cache.put(SELECTORS_PREFETCH_KEY, Urls.urls.selectors, data, 'prefetch');
        } catch {
            // Best-effort: a failed prefetch just leaves the previous cached snapshot in place.
        }
    }
}
